'use client';

import React, { createElement } from 'react';

import { Button, Skeleton, Typography } from 'lighthouse';

import AddToCartButton from '@/widgets/AddToCartButton';

import { rialToToman } from '@/utilities/Financial';
import { getFulfillmentIcon, getFulfillmentLabel, isZeroLaborFeeFulfillment } from '@/utilities/fulfillment';
import { withSeparator } from '@/utilities/withSeparator';

import { CAR_BATTERY_VEHICLE_TYPE_ID, MOTOR_BATTERY_VEHICLE_TYPE_ID } from '@/constants/batteryProduct';
import { productRequestRouteByVehicleType, RELATED_PRODUCTS_SECTION_ID } from '@/constants/product';

import { MobileBuyBarProps } from './models';

import { ShoppingBag } from 'iconsax-react';

export const MobileBuyBar = ({
    winner,
    allStores,
    available,
    isPriceLoading,
    category,
    vehicleType,
    uuid,
    selectedFulfillmentProfile,
    onOpenDeliverySheet
}: MobileBuyBarProps) => {
    const hasSelectedFulfillment = !!selectedFulfillmentProfile;
    const selectedLaborFee = isZeroLaborFeeFulfillment(selectedFulfillmentProfile?.fulfillmentType) ? 0 : selectedFulfillmentProfile?.laborFee || 0;
    const totalPriceWithFulfillment = (winner.sellingPrice || winner.price) + selectedLaborFee;

    if (!isPriceLoading && !available) {
        return (
            <div
                className='fixed bottom-0 left-0 right-0 z-20 border-t border-solid border-gray-200 bg-white/95 shadow-[0_-4px_16px_rgba(0,0,0,0.08)]
                    backdrop-blur [touch-action:manipulation] lg:hidden'>
                <div className='flex min-h-14 items-center justify-between gap-3 p-3'>
                    <Typography variant='body2' fontWeight={600} className='shrink-0 !text-red-600'>
                        {winner.availableQuantity > 0 ? 'خارج از محدوده پوشش' : 'این کالا ناموجود است'}
                    </Typography>
                    <div className='min-w-40 shrink-0'>
                        <Button
                            variant='contained'
                            color='primary'
                            size='large'
                            fullWidth
                            startIcon={<ShoppingBag size={18} color='currentcolor' />}
                            onClick={() => {
                                document.getElementById(RELATED_PRODUCTS_SECTION_ID)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                            }}
                            className='!min-h-11'>
                            محصولات مرتبط
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div
            className='fixed bottom-0 left-0 right-0 z-20 border-t border-solid border-gray-200 bg-white/95 shadow-[0_-4px_16px_rgba(0,0,0,0.08)]
                backdrop-blur [touch-action:manipulation] lg:hidden'>
            <div className='flex flex-col p-3 py-0'>
                {isPriceLoading ? (
                    <div className='flex min-h-11 w-full items-center justify-between gap-3 px-2 py-1.5'>
                        <Skeleton variant='rectangular' width={120} height={22} className='rounded-lg' />
                        <Skeleton variant='rectangular' width={96} height={22} className='rounded-lg' />
                    </div>
                ) : hasSelectedFulfillment ? (
                    <div
                        aria-haspopup='dialog'
                        aria-label='تغییر روش تحویل'
                        onClick={onOpenDeliverySheet}
                        className='flex min-h-11 w-full cursor-pointer items-center justify-between gap-3 rounded-lg px-2 py-1.5 text-right
                            transition-colors duration-200 hover:bg-primary/[0.04] active:bg-primary/[0.08]'>
                        <span className='flex min-w-0 items-center gap-2'>
                            <span className='flex size-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary'>
                                {createElement(getFulfillmentIcon(selectedFulfillmentProfile?.fulfillmentType), {
                                    color: 'currentcolor',
                                    className: 'size-4'
                                })}
                            </span>
                            <span className='flex min-w-0 flex-col'>
                                <Typography
                                    variant='caption'
                                    fontWeight={600}
                                    color='blackHighEmphasis'
                                    className='flex items-center gap-2 truncate !text-[11px]'>
                                    {getFulfillmentLabel(selectedFulfillmentProfile?.fulfillmentType)}
                                </Typography>
                            </span>
                        </span>
                        <span className='flex shrink-0 flex-col items-end'>
                            <Typography variant='subtitle2' fontWeight={700} color='primary' className='!text-sm'>
                                {withSeparator(rialToToman(totalPriceWithFulfillment))}
                                <span className='mr-1 text-[11px] font-semibold text-blackHighEmphasis'>تومان</span>
                            </Typography>
                        </span>
                    </div>
                ) : null}
                <hr className='my-0 w-full border-dashed opacity-10' />
                <div className='flex min-h-11 items-center justify-between gap-3'>
                    <div className='flex min-w-0 flex-1 items-center gap-1.5 px-2'>
                        <Typography variant='caption' color='blackLowEmphasis' className='shrink-0 !text-[11px]'>
                            فروشنده:
                        </Typography>
                        <Typography variant='caption' fontWeight={600} color='blackHighEmphasis' className='truncate !text-[11px]'>
                            {winner.storeName}
                        </Typography>
                    </div>
                    <div className='min-w-40 shrink-0'>
                        <AddToCartButton
                            size='large'
                            isMainVendor={category.slug === CAR_BATTERY_VEHICLE_TYPE_ID || category.slug === MOTOR_BATTERY_VEHICLE_TYPE_ID}
                            available={available}
                            allStores={allStores}
                            winner={winner}
                            href={available ? `/${vehicleType}${productRequestRouteByVehicleType[vehicleType]}?productId=${uuid}` : ''}
                            isPriceLoading={isPriceLoading || false}
                            selectedFulfillmentProfile={selectedFulfillmentProfile}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};
